import { PASSWORD_RESET_TTL_MINUTES } from '@epc/contracts';
import { Link, useSearch } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { Button } from '../../ui/button';
import { ErrorBox } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { AuthLayout } from './AuthLayout';

const TTL = PASSWORD_RESET_TTL_MINUTES === 60 ? 'один час' : `${PASSWORD_RESET_TTL_MINUTES} мин.`;

const linkClass = 'inline-flex min-h-11 items-center font-medium text-ink underline underline-offset-4';

/**
 * «Забыли пароль?» (ДС-19): человек вводит рабочую почту, портал присылает ссылку для нового пароля. Ответ один
 * и тот же для любой почты — по странице нельзя узнать, есть ли адрес в портале.
 */
export function ForgotPasswordPage() {
  // Почта, уже введённая на странице входа
  const search = useSearch({ strict: false }) as { email?: string };
  const [email, setEmail] = useState(search.email ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setError(null);
    const value = email.trim();
    if (!value) {
      setErrors({ email: 'Укажите почту, с которой входите в портал' });
      return;
    }
    setBusy(true);
    try {
      await api('/auth/password-reset', { body: { email: value } });
      setSentTo(value);
    } catch (err) {
      if (err instanceof ApiError) setErrors(err.fieldErrors());
      setError(err instanceof Error ? err : new Error('Не удалось отправить ссылку'));
    } finally {
      setBusy(false);
    }
  }

  if (sentTo) {
    return (
      <AuthLayout title="Проверьте почту">
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3 rounded-md bg-success-soft px-4 py-3 text-[14px] text-ink">
            <MailCheck className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
            <p>
              Если почта <b className="break-all">{sentTo}</b> есть в портале, на неё придёт письмо со ссылкой
              для нового пароля. Ссылка действует {TTL} и срабатывает один раз.
            </p>
          </div>
          <p className="text-[13px] text-ink-2">
            Письма нет через несколько минут — проверьте папку «Спам» и правильность адреса. Если почта
            сменилась или письма так и нет, обратитесь к администратору своей компании.
          </p>
          <div className="flex flex-wrap items-center gap-x-5">
            <Link to="/login" search={{ email: sentTo }} className={linkClass}>
              Вернуться ко входу
            </Link>
            <button
              type="button"
              className="inline-flex min-h-11 items-center text-[14px] font-medium text-ink-2 underline-offset-4 hover:text-ink hover:underline"
              onClick={() => {
                setSentTo(null);
                setError(null);
              }}
            >
              Указать другую почту
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Восстановление пароля"
      subtitle="Укажите рабочую почту, с которой входите в портал, — пришлём ссылку, по которой можно задать новый пароль."
    >
      <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
        <Field label="Почта" htmlFor="email" error={errors.email}>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={!!errors.email}
            required
          />
        </Field>
        <ErrorBox error={Object.keys(errors).length ? null : error} />
        <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!email.trim()}>
          Отправить ссылку
        </Button>
        <p className="text-[13px] text-ink-2">
          Вспомнили пароль?{' '}
          <Link to="/login" search={{ email: email.trim() || undefined }} className={linkClass}>
            Войти
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
